import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';

import { applyLocaleDocument } from './apply-locale';
import { resolveEnabledDisplayLocales, resolveLocaleWithinEnabled } from './display-locales';
import { APP_CONFIG_KEY, type AppConfig } from '@/types/AppConfig.type';
import { isAppLocale, type AppLocale } from './locales';
import { persistDisplayLocale, resolveDisplayLocale } from './resolve-locale';

type Catalogue = Record<string, string>;

let localeRequestId = 0;

async function loadCatalogue(locale: AppLocale): Promise<Catalogue> {
  switch (locale) {
    case 'en': {
      const module = await import('./locales/en.json');
      return module.default;
    }
    case 'tr': {
      const module = await import('./locales/tr.json');
      return module.default;
    }
    case 'zh-Hans': {
      const module = await import('./locales/zh-Hans.json');
      return module.default;
    }
    case 'bn': {
      const module = await import('./locales/bn.json');
      return module.default;
    }
    default: {
      const exhaustive: never = locale;
      return exhaustive;
    }
  }
}

function registerCatalogue(locale: AppLocale, catalogue: Catalogue): void {
  if (i18n.hasResourceBundle(locale, 'translation')) {
    return;
  }
  i18n.addResourceBundle(locale, 'translation', catalogue, true, true);
}

export async function initI18n(): Promise<typeof i18n> {
  const locale = resolveDisplayLocale();
  const english = await loadCatalogue('en');
  const active = locale === 'en' ? english : await loadCatalogue(locale);
  if (!i18n.isInitialized) {
    await i18n.use(initReactI18next).init({
      lng: locale,
      fallbackLng: 'en',
      keySeparator: false,
      nsSeparator: false,
      interpolation: {
        escapeValue: false,
      },
      resources: {
        en: { translation: english },
        ...(locale === 'en' ? {} : { [locale]: { translation: active } }),
      },
      returnNull: false,
      returnEmptyString: false,
    });
    applyLocaleDocument(locale);
    return i18n;
  }
  registerCatalogue('en', english);
  if (locale !== 'en') {
    registerCatalogue(locale, active);
  }
  await i18n.changeLanguage(locale);
  applyLocaleDocument(locale);
  return i18n;
}

export async function setLocale(locale: AppLocale): Promise<void> {
  const requestId = ++localeRequestId;
  const catalogue = await loadCatalogue(locale);
  registerCatalogue(locale, catalogue);
  if (requestId !== localeRequestId) {
    return;
  }
  await i18n.changeLanguage(locale);
  if (requestId !== localeRequestId) {
    return;
  }
  applyLocaleDocument(locale);
  persistDisplayLocale(locale);
}

export type DisplayLanguagesConfig = Pick<
  AppConfig,
  (typeof APP_CONFIG_KEY)['enableDisplayLanguages'] | (typeof APP_CONFIG_KEY)['displayLocales']
>;

export async function applyDisplayLocalesFromConfig(
  appConfig: DisplayLanguagesConfig
): Promise<void> {
  const enabled = resolveEnabledDisplayLocales(
    appConfig[APP_CONFIG_KEY.enableDisplayLanguages],
    appConfig[APP_CONFIG_KEY.displayLocales]
  );
  const current = isAppLocale(i18n.language) ? i18n.language : resolveDisplayLocale();
  const resolved = resolveLocaleWithinEnabled(current, enabled);
  if (current === resolved) {
    return;
  }
  await setLocale(resolved);
}

export default i18n;
